import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { ConversationService } from '../services/conversationService';
import { MessageService } from '../services/messageService';
import {
  Conversation,
  Message,
  CreateConversationParams,
  CreateMessageParams,
  UpdateMemberInfoParams,
} from '../types/message';
import { collection, query, where, orderBy, onSnapshot, limit, getFirestore } from 'firebase/firestore';

interface MessagingContextType {
  // State
  conversations: Conversation[];
  activeConversationId: string | null;
  activeConversation: Conversation | null;
  messages: Message[];
  loading: boolean;
  error: string | null;

  // Actions
  setActiveConversation: (conversationId: string | null) => void;
  createConversation: (params: CreateConversationParams) => Promise<string>;
  sendMessage: (params: CreateMessageParams) => Promise<string>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
  markConversationAsRead: (conversationId: string) => Promise<void>;
  updateMemberInfo: (conversationId: string, updates: UpdateMemberInfoParams) => Promise<void>;
  archiveConversation: (conversationId: string) => Promise<void>;
  unarchiveConversation: (conversationId: string) => Promise<void>;
  muteConversation: (conversationId: string, muted: boolean) => Promise<void>;
  refreshConversations: () => Promise<void>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(undefined);

export const useMessaging = (): MessagingContextType => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within MessagingProvider');
  }
  return context;
};

interface MessagingProviderProps {
  children: ReactNode;
}

export const MessagingProvider: React.FC<MessagingProviderProps> = ({ children }) => {
  const { authState } = useAuth();
  const user = authState.user;
  const db = getFirestore();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for conversations
  useEffect(() => {
    if (!user) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid),
      orderBy('lastActivity', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const convos = snapshot.docs.map(doc => {
          const data = doc.data();

          // Convert Firestore timestamps to Date objects
          const memberInfo: Record<string, any> = {};
          if (data.memberInfo) {
            Object.entries(data.memberInfo).forEach(([userId, info]: [string, any]) => {
              memberInfo[userId] = {
                ...info,
                joined: info.joined?.toDate(),
                lastRead: info.lastRead?.toDate(),
              };
            });
          }

          return {
            id: doc.id,
            ...data,
            lastActivity: data.lastActivity?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            memberInfo,
            lastMessage: data.lastMessage ? {
              ...data.lastMessage,
              timestamp: data.lastMessage.timestamp?.toDate(),
            } : undefined,
          } as Conversation;
        });

        setConversations(convos);
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to conversations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, db]);

  // Real-time listener for messages in active conversation
  useEffect(() => {
    if (!activeConversationId) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, 'messages'),
      where('conversationId', '==', activeConversationId),
      where('deleted', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            editedAt: data.editedAt?.toDate(),
            deletedAt: data.deletedAt?.toDate(),
          } as Message;
        });

        setMessages(msgs);
      },
      (err) => {
        console.error('Error listening to messages:', err);
        setError(err.message);
      }
    );

    return () => unsubscribe();
  }, [activeConversationId, db]);

  // Get active conversation object
  const activeConversation = conversations.find(c => c.id === activeConversationId) || null;

  // Actions
  const setActiveConversation = useCallback((conversationId: string | null) => {
    setActiveConversationId(conversationId);
    if (conversationId && user) {
      // Mark as read when opening conversation
      ConversationService.markAsRead(conversationId, user.uid);
    }
  }, [user]);

  const createConversation = useCallback(async (params: CreateConversationParams): Promise<string> => {
    if (!user) throw new Error('Not authenticated');
    return await ConversationService.createConversation(params, user.uid);
  }, [user]);

  const sendMessage = useCallback(async (params: CreateMessageParams): Promise<string> => {
    if (!user) throw new Error('Not authenticated');
    return await MessageService.sendMessage(params, user.uid);
  }, [user]);

  const editMessage = useCallback(async (messageId: string, content: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await MessageService.editMessage(messageId, content, user.uid);
  }, [user]);

  const deleteMessage = useCallback(async (messageId: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await MessageService.deleteMessage(messageId, user.uid);
  }, [user]);

  const addReaction = useCallback(async (messageId: string, emoji: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await MessageService.addReaction(messageId, emoji, user.uid);
  }, [user]);

  const removeReaction = useCallback(async (messageId: string, emoji: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await MessageService.removeReaction(messageId, emoji, user.uid);
  }, [user]);

  const markConversationAsRead = useCallback(async (conversationId: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await ConversationService.markAsRead(conversationId, user.uid);
  }, [user]);

  const updateMemberInfo = useCallback(async (
    conversationId: string,
    updates: UpdateMemberInfoParams
  ): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await ConversationService.updateMemberInfo(conversationId, user.uid, updates);
  }, [user]);

  const archiveConversation = useCallback(async (conversationId: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await ConversationService.updateMemberInfo(conversationId, user.uid, { archived: true });
  }, [user]);

  const unarchiveConversation = useCallback(async (conversationId: string): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await ConversationService.updateMemberInfo(conversationId, user.uid, { archived: false });
  }, [user]);

  const muteConversation = useCallback(async (conversationId: string, muted: boolean): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await ConversationService.updateMemberInfo(conversationId, user.uid, { muted });
  }, [user]);

  const refreshConversations = useCallback(async (): Promise<void> => {
    // Firestore listeners auto-refresh, but this can force a manual refresh if needed
    setLoading(true);
    // The listener will automatically update
    setLoading(false);
  }, []);

  const value: MessagingContextType = {
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    loading,
    error,
    setActiveConversation,
    createConversation,
    sendMessage,
    editMessage,
    deleteMessage,
    addReaction,
    removeReaction,
    markConversationAsRead,
    updateMemberInfo,
    archiveConversation,
    unarchiveConversation,
    muteConversation,
    refreshConversations,
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};
